import { buildDemoSnapshot, INITIAL_FLOW_STATE, syncConflictReason } from './model.ts';
import type { DemoAction } from './actions.ts';
import type { DemoPersistedState, DemoSnapshot, ReportStatus } from './types.ts';

function clonePlain<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

export function createInitialState(): DemoPersistedState {
  return {
    version: 1,
    quoteHasProbeCheck: false,
    dispatchAssignment: null,
    flowState: clonePlain(INITIAL_FLOW_STATE),
  };
}

export function normalizeState(value: unknown): DemoPersistedState {
  if (!value || typeof value !== 'object') return createInitialState();
  const candidate = value as Partial<DemoPersistedState>;

  return {
    version: 1,
    quoteHasProbeCheck: Boolean(candidate.quoteHasProbeCheck),
    dispatchAssignment:
      candidate.dispatchAssignment &&
      typeof candidate.dispatchAssignment === 'object' &&
      typeof candidate.dispatchAssignment.ownerName === 'string' &&
      typeof candidate.dispatchAssignment.scheduledWindow === 'string'
        ? {
            ownerName: candidate.dispatchAssignment.ownerName,
            scheduledWindow: candidate.dispatchAssignment.scheduledWindow,
          }
        : null,
    flowState: {
      ...clonePlain(INITIAL_FLOW_STATE),
      ...(candidate.flowState || {}),
    },
  };
}

export function persistedStateFromSnapshot(
  snapshot: Pick<DemoSnapshot, 'quoteHasProbeCheck' | 'dispatchAssignment' | 'flowState'>,
): DemoPersistedState {
  return normalizeState({
    version: 1,
    quoteHasProbeCheck: snapshot.quoteHasProbeCheck,
    dispatchAssignment: snapshot.dispatchAssignment,
    flowState: snapshot.flowState,
  });
}

export function applyDemoAction(state: DemoPersistedState, action: DemoAction): DemoPersistedState {
  switch (action.type) {
    case 'demo:reset':
      return createInitialState();
    case 'quote:add-probe-check':
      return { ...state, quoteHasProbeCheck: true };
    case 'quote:convert':
      return {
        ...state,
        flowState: { ...state.flowState, quoteConverted: true },
      };
    case 'dispatch:assign':
      return {
        ...state,
        flowState: { ...state.flowState, quoteConverted: true },
        dispatchAssignment: {
          ownerName: action.ownerName,
          scheduledWindow: action.scheduledWindow,
        },
      };
    case 'job:start':
      return {
        ...state,
        flowState: {
          ...state.flowState,
          quoteConverted: true,
          onSite: true,
        },
      };
    case 'job:capture-photos':
      return {
        ...state,
        flowState: { ...state.flowState, photosCaptured: true },
      };
    case 'job:finalize-voice':
      return {
        ...state,
        flowState: { ...state.flowState, voiceCaptured: true },
      };
    case 'job:capture-signature':
      return {
        ...state,
        flowState: { ...state.flowState, signatureCaptured: true },
      };
    case 'sync:mark-conflict':
      return {
        ...state,
        flowState: {
          ...state.flowState,
          syncStatus: 'conflict',
          syncConflictReason: syncConflictReason,
        },
      };
    case 'sync:set-retrying':
      return {
        ...state,
        flowState: {
          ...state.flowState,
          syncStatus: 'retrying',
        },
      };
    case 'sync:complete-retry':
      return {
        ...state,
        flowState: {
          ...state.flowState,
          syncStatus: 'synced',
          syncConflictReason: null,
        },
      };
    case 'report:submit': {
      const nextStatus: ReportStatus = state.flowState.reportStatus === 'rejected' ? 'resubmitted' : 'submitted';
      return {
        ...state,
        flowState: {
          ...state.flowState,
          reportStatus: nextStatus,
          rejectionReason: null,
        },
      };
    }
    case 'report:validate':
      return {
        ...state,
        flowState: {
          ...state.flowState,
          reportStatus: 'validated',
        },
      };
    case 'report:reject':
      return {
        ...state,
        flowState: {
          ...state.flowState,
          reportStatus: 'rejected',
          rejectionReason: action.rejectionReason,
        },
      };
    case 'invoice:create':
      return {
        ...state,
        flowState: {
          ...state.flowState,
          invoiceCreated: true,
        },
      };
  }
}

export function snapshotFromState(state: DemoPersistedState): DemoSnapshot {
  return buildDemoSnapshot(state);
}
